import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Check, Bell, LayoutDashboard, Target } from "lucide-react";
import { useBranch } from "../../context/BranchContext";
import { useAuth } from "../../context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";

const TIERS = [
  { key: "score", label: "Growth Score" },
  { key: "schedule", label: "Growth Schedule" },
  { key: "managed", label: "Growth Managed" },
];

export default function TopBar({ title, subtitle }) {
  const { branches, branch, setBranchId } = useBranch();
  const { user, setTier, logout, canGroup, isGroup, setViewAs } = useAuth();
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-20 flex items-center gap-4 px-8 py-4 border-b backdrop-blur-md" style={{ background: "rgba(247,248,246,.85)", borderColor: "#E7E8E4" }}>
      <div className="min-w-0">
        {title && <h1 className="text-lg font-bold tracking-tight text-ink truncate">{title}</h1>}
        {subtitle && <p className="text-sm text-ink-2 truncate">{subtitle}</p>}
      </div>

      <div className="ml-auto flex items-center gap-2.5">
        {/* View-as toggle (demo) — branch vs group oversight */}
        {canGroup && (
          <div className="flex items-center rounded-full border p-0.5" style={{ borderColor: "#E7E8E4", background: "#fff" }} data-testid="view-as-toggle">
            <button data-testid="view-as-branch" onClick={() => { setViewAs("branch"); navigate("/app"); }}
              className="rounded-full px-3 py-1.5 text-sm font-semibold transition-colors" style={!isGroup ? { background: "var(--grad-ink)", color: "#fff" } : { color: "#6B6E62" }}>Branch</button>
            <button data-testid="view-as-group" onClick={() => { setViewAs("group"); navigate("/app/group"); }}
              className="rounded-full px-3 py-1.5 text-sm font-semibold transition-colors" style={isGroup ? { background: "var(--grad-ink)", color: "#fff" } : { color: "#6B6E62" }}>Group</button>
          </div>
        )}

        {/* Branch selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="btn-secondary focus-ring" data-testid="branch-selector">
              {branch?.name || "Padstow"} <ChevronDown size={15} strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Gilmours · Branches</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {branches.map((b) => (
              <DropdownMenuItem key={b.id} onClick={() => setBranchId(b.id)} data-testid={`branch-option-${b.id}`} className="flex items-center justify-between">
                <span>{b.name}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs font-bold text-ink-3">{b.current_score}</span>
                  {branch?.id === b.id && <Check size={14} strokeWidth={2} color="#678A16" />}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Demo tier switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="btn-secondary focus-ring" data-testid="tier-switcher">
              Plan: {TIERS.find((t) => t.key === (user?.tier || "score"))?.label.replace("Growth ", "")} <ChevronDown size={15} strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Demo plan (feature gating)</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {TIERS.map((t) => (
              <DropdownMenuItem key={t.key} disabled={switching} onClick={async () => { setSwitching(true); await setTier(t.key); setSwitching(false); }} data-testid={`tier-option-${t.key}`} className="flex items-center justify-between">
                {t.label}
                {(user?.tier || "score") === t.key && <Check size={14} strokeWidth={2} color="#678A16" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <button className="btn-ghost focus-ring relative" data-testid="notifications">
          <Bell size={18} strokeWidth={1.5} />
          <span className="absolute top-1.5 right-2 w-1.5 h-1.5 rounded-full" style={{ background: "#93C020" }} />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-white transition-colors focus-ring" data-testid="user-menu">
              <span className="grid place-items-center w-8 h-8 rounded-full text-white text-xs font-bold" style={{ background: "#678A16" }}>
                {(user?.name || "S").split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="truncate">{user?.name}<div className="text-xs font-normal text-ink-3 truncate">{user?.email}</div></DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/ops")} data-testid="goto-ops"><LayoutDashboard size={14} strokeWidth={1.75} className="mr-2" /> Managed Operations</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/sales")} data-testid="goto-sales"><Target size={14} strokeWidth={1.75} className="mr-2" /> Sales Director</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn">Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
